import { z } from 'zod';

/**
 * Verwaltung der Partnerzugänge (ADR 0085) — die INTERNE Sicht darauf.
 * Angelegt und gepflegt wird ausschließlich von Administratoren
 * (`admin.manage`, Entscheidung Tech-Lead 2026-08-06): Ein Partnerzugang ist
 * ein Zugang von außen; wer ihn vergibt, vergibt Einsicht in unsere Daten.
 */

/** Ein Partner (Firma), für den es Portal-Zugänge gibt. */
export const partnerAccountResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Verknüpfter Kontakt im CRM (weiche Referenz, kein Fremdschlüssel — §5). */
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  isActive: z.boolean(),
  canSeeForecast: z.boolean(),
  canSeeAssembly: z.boolean(),
  /** Wie viele aktive Zugänge hängen daran? */
  userCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});
export type PartnerAccountResponse = z.infer<typeof partnerAccountResponseSchema>;

export const createPartnerAccountSchema = z.object({
  name: z.string().trim().min(1, 'Name ist erforderlich').max(200),
  customerId: z.string().uuid().nullable().optional(),
  canSeeForecast: z.boolean().optional(),
  canSeeAssembly: z.boolean().optional(),
});
export type CreatePartnerAccountInput = z.infer<typeof createPartnerAccountSchema>;

export const updatePartnerAccountSchema = createPartnerAccountSchema.partial().extend({
  /** Deaktivieren entzieht den Zugang SOFORT — laufende Sitzungen enden mit der nächsten Anfrage. */
  isActive: z.boolean().optional(),
});
export type UpdatePartnerAccountInput = z.infer<typeof updatePartnerAccountSchema>;

/** Ein einzelner Portal-Zugang (Person) unterhalb eines Partners. */
export const partnerUserResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string().nullable(),
  isActive: z.boolean(),
  /**
   * Hat die Person den zweiten Faktor schon eingerichtet (ADR 0034)? Beides
   * `false` heißt: Der Erstlogin steht noch aus — die praktische Frage bei der
   * Betreuung eines neuen Zugangs. Ein „zuletzt angemeldet" gibt es bewusst
   * nicht: Das Datenmodell führt es nicht, und ein immer leeres Feld wäre
   * irreführender als gar keins.
   */
  isTotpEnabled: z.boolean(),
  hasPasskey: z.boolean(),
});
export type PartnerUserResponse = z.infer<typeof partnerUserResponseSchema>;

export const createPartnerUserSchema = z.object({
  email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse angeben'),
  name: z.string().trim().max(200).optional(),
});
export type CreatePartnerUserInput = z.infer<typeof createPartnerUserSchema>;

export const updatePartnerUserSchema = z.object({
  name: z.string().trim().max(200).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpdatePartnerUserInput = z.infer<typeof updatePartnerUserSchema>;

/**
 * Antwort beim Anlegen eines Zugangs bzw. beim Zurücksetzen des Passworts.
 * Das Anfangspasswort wird serverseitig erzeugt und **genau einmal**
 * zurückgegeben — es ist nirgends gespeichert (nur der scrypt-Hash) und lässt
 * sich später nicht mehr anzeigen, nur neu setzen.
 */
export const partnerUserCredentialsSchema = z.object({
  user: partnerUserResponseSchema,
  initialPassword: z.string(),
});
export type PartnerUserCredentials = z.infer<typeof partnerUserCredentialsSchema>;
